import React from "react";
import { Typography, List, Space, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { formatEuro, formatWeight } from "../PricingCalculatorHelpers";
import { CalculatedPrices } from "../PricingCalculatorTypes";
import { Tariff } from "../../../hooks/useTariffs";

const { Text } = Typography;

interface TransportCostsSectionProps {
  calculatedPrices: CalculatedPrices;
  applicableTariff: Tariff | null;
}

export const TransportCostsSection: React.FC<TransportCostsSectionProps> = ({
  calculatedPrices,
  applicableTariff,
}) => {
  const { state } = useBookingWizard();
  const { t } = useTranslation();

  return (
    <div style={{ marginBottom: 16 }}>
      <Text strong>
        {t(
          "bookingWizard.pricingCalculator.transportCostsSectionTitle",
          "Transport"
        )}
      </Text>
      <List size="small" itemLayout="horizontal" style={{ marginTop: 8 }}>
        {/* Air freight specific information */}
        {state.transport_type === 1 && (
          <>
            {calculatedPrices.pricePerKg > 0 && (
              <List.Item
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Space
                  direction="vertical"
                  size={0}
                  style={{ alignItems: "flex-start" }}
                >
                  <Space>
                    <Text>
                      {t(
                        "bookingWizard.pricingCalculator.pricePerKilogram",
                        "Price per kilogram"
                      )}
                    </Text>
                    <Tooltip
                      title={t(
                        "bookingWizard.pricingCalculator.pricePerKilogramTooltip",
                        "Base rate per kilogram of freight"
                      )}
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>
                  {applicableTariff && (
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {applicableTariff.origin_port.name} →{" "}
                      {applicableTariff.destination_port.name}
                    </Text>
                  )}
                </Space>
                <Text>{formatEuro(calculatedPrices.pricePerKg)}</Text>
              </List.Item>
            )}

            {calculatedPrices.volumeWeight > 0 && (
              <List.Item
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Space>
                  <Text>
                    {t(
                      "bookingWizard.pricingCalculator.volumeWeight",
                      "Volume weight"
                    )}
                  </Text>
                  <Tooltip
                    title={t(
                      "bookingWizard.pricingCalculator.volumeWeightTooltip",
                      "The volumetric weight calculated from dimensions (1 CBM = 167 kg)"
                    )}
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
                <Text>{formatWeight(calculatedPrices.volumeWeight)}</Text>
              </List.Item>
            )}

            {calculatedPrices.chargeableWeight > 0 && (
              <List.Item
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <Space>
                  <Text>
                    {t(
                      "bookingWizard.pricingCalculator.chargeableWeight",
                      "Chargeable weight"
                    )}
                  </Text>
                  <Tooltip
                    title={t(
                      "bookingWizard.pricingCalculator.chargeableWeightTooltip",
                      "The higher of actual weight and volume weight. For air freight, we compare the actual gross weight with the volumetric weight (calculated as 1 CBM = 167 kg) and charge based on whichever is higher."
                    )}
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
                <Text>{formatWeight(calculatedPrices.chargeableWeight)}</Text>
              </List.Item>
            )}
          </>
        )}

        <List.Item style={{ display: "flex", justifyContent: "space-between" }}>
          <Space
            direction="vertical"
            size={0}
            style={{ alignItems: "flex-start" }}
          >
            <Space>
              <Text>
                {t(
                  "bookingWizard.pricingCalculator.transportCost",
                  "Transport cost"
                )}
              </Text>
              <Tooltip
                title={t(
                  "bookingWizard.pricingCalculator.transportCostTooltip",
                  "Base transportation cost"
                )}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
            {applicableTariff && (
              <Text type="secondary" style={{ fontSize: "12px" }}>
                {applicableTariff.origin_port.name} →{" "}
                {applicableTariff.destination_port.name}
                {applicableTariff.bookings_type &&
                  ` (${applicableTariff.bookings_type.name})`}
              </Text>
            )}
          </Space>
          <Text>{formatEuro(calculatedPrices.transportCost)}</Text>
        </List.Item>

        {/* Pickup Fee */}
        {calculatedPrices.pickupFee > 0 && (
          <List.Item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Space>
              <Text>{t("pickupFee")}</Text>
              <Tooltip title={t("pickupFeeTooltip")}>
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
            <Text>{formatEuro(calculatedPrices.pickupFee)}</Text>
          </List.Item>
        )}

        {/* Export Clearance License Fee */}
        {calculatedPrices.exportClearanceLicense > 0 && (
          <List.Item
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <Space>
              <Text>{t("exportClearanceLicense")}</Text>
              <Tooltip title={t("exportClearanceLicenseTooltip")}>
                <InfoCircleOutlined />
              </Tooltip>
            </Space>
            <Text>{formatEuro(calculatedPrices.exportClearanceLicense)}</Text>
          </List.Item>
        )}
      </List>
    </div>
  );
};















